import { PDKExpandListIcon } from '@/components/ui/pdk-icons';
import { pluralize } from '@/lib/utils';
import type { Audit } from '@/models/registry';
import type { AuditLog } from '@/models/types';
import { useMemo, useState } from 'react';
import AuditLine from './audit-line';
import type { AuditTrailLogs } from './audit-trail';

export type CombinedAuditLog = {
  type: 'combined-audit';
  groupId: number;
  ids: string[];
};

const GROUPING_THRESHOLD = 5;

export const getCombinedAuditLogs = (
  sortedAuditLogs: AuditLog[],
  audits: Audit[],
) => {
  // UX NOTE: if there are `GROUPING_THRESHOLD` or more audits in a row
  // then we will group them together and show a single audit line
  // unless the user has explicitly clicked on the audit line to expand it
  const groupedAudits: AuditTrailLogs = [];

  let startIndexOfSequence: number | null = null;
  let endIndexOfSequence: number | null = null;

  let groupId = 1;

  for (let i = 0; i < sortedAuditLogs.length; i++) {
    const currentAudit = sortedAuditLogs[i];
    const nextAudit = sortedAuditLogs[i + 1];

    if (currentAudit.type === 'comment') {
      // we will not group comments
      groupedAudits.push(currentAudit);

      // reset the startIndexOfSequence and endIndexOfSequence
      startIndexOfSequence = null;
      endIndexOfSequence = null;

      continue;
    }

    if (startIndexOfSequence === null && currentAudit.type === 'audit') {
      startIndexOfSequence = i;
    }

    if (
      startIndexOfSequence !== null &&
      currentAudit.type === 'audit' &&
      nextAudit?.type !== 'audit'
    ) {
      endIndexOfSequence = i;
    }

    if (endIndexOfSequence !== null && startIndexOfSequence !== null) {
      const numberOfAuditsInSequence =
        endIndexOfSequence - startIndexOfSequence + 1;

      if (numberOfAuditsInSequence < GROUPING_THRESHOLD) {
        // These will not be grouped together
        // so we will just add them to the groupedAudits array
        for (let j = startIndexOfSequence; j <= endIndexOfSequence; j++) {
          const audit = audits.find((a) => a.id === sortedAuditLogs[j].id);
          if (!audit) continue;

          groupedAudits.push({
            type: 'audit',
            id: audit.id,
            deal_id: audit.deal_id,
          });
        }
      } else {
        // we will group these audits
        const combinedAudit: CombinedAuditLog = {
          type: 'combined-audit',
          groupId,
          ids: [],
        };

        for (let j = startIndexOfSequence; j <= endIndexOfSequence; j++) {
          const audit = audits.find((a) => a.id === sortedAuditLogs[j].id);
          if (!audit) continue;
          combinedAudit.ids.push(audit.id);
        }
        groupedAudits.push(combinedAudit);

        // reset the startIndexOfSequence and endIndexOfSequence
        startIndexOfSequence = null;
        endIndexOfSequence = null;

        // increment the groupId
        groupId++;
      }
    }
  }

  return groupedAudits;
};

const CombinedAuditLine = ({
  auditLog,
  audits,
}: {
  auditLog: CombinedAuditLog;
  audits: Audit[];
}) => {
  const { ids } = auditLog;

  const [isExpanded, setIsExpanded] = useState(false);

  const combinedAudits = useMemo(() => {
    return ids.map((id) => audits.find((a) => a.id === id));
  }, [ids, audits]);

  const combinedAuditsSummary = useMemo(() => {
    return combinedAudits
      .map((audit) => audit?.field.split('_').join(' '))
      .join(', ');
  }, [combinedAudits]);

  if (isExpanded) {
    return (
      <>
        {ids.map((id) => {
          const audit = audits?.find((a) => a.id === id);
          if (!audit) return null;
          return <AuditLine key={audit.id} audit={audit} />;
        })}
      </>
    );
  }

  return (
    <div
      className="flex flex-row gap-2 items-center px-4 cursor-pointer group rounded-md transition-colors"
      onClick={() => setIsExpanded(true)}
    >
      <PDKExpandListIcon className="cursor-pointer" />
      <div className="group-hover text-muted-foreground group-hover:text-foreground">
        Show {ids.length} {pluralize(ids.length, 'event', 'events')}: changed{' '}
        {combinedAuditsSummary}
      </div>
    </div>
  );
};

export default CombinedAuditLine;
